<template>
  <v-row no-gutters>
    <v-col cols="12">
      <sidebar-row class="pr-5">
        <v-row no-gutters>
          <v-col cols="auto">
            <base-button translation-path="sidebar.importDxf.importFile" @click="showImportDxf"></base-button>
          </v-col>
          <v-col>
            <data-input
              v-bind="searchInput"
              v-model="searchQuery"
              class="searchInput"
              prepend-inner-icon="mdi-magnify"
            ></data-input>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row class="pr-5" v-if="layersListFetched">
        <dxf-layers-list :layers="filteredLayers" :layers-all="layers"> </dxf-layers-list>
      </sidebar-row>
      <main-loader v-else />
    </v-col>
    <main-input-dialog
      v-if="isImportDxfLoaded"
      :is-visible.sync="isImportDxfVisible"
      :title-translation="$i18n.t('dialog.importDxfFile')"
      agree-text="dialog.body.import"
      @agree="handleImportDxf"
      :inputs="importDxfInputs"
      ref="dxfAdding"
    >
      <template #file.input>
        <v-col cols="6">
          <file-input
            ref="dxfFile"
            :options="dropzoneOptions"
            :show-added-files="true"
            :expanded-files-extensions="filesExtensions"
            :events="{
              'vdropzone-sending-multiple': sendingEvent,
              'vdropzone-success-multiple': onUploadSuccess,
            }"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { debounce } from 'lodash';

import DataInput from '@/components/DataInput';
import DxfLayersList from '@/components/DxfLayersList';
import FileInput from '@/components/FileInput';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'DxfLayersListWrapper',
  components: {
    DataInput,
    DxfLayersList,
    FileInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLoader,
    SidebarRow,
  },
  data: vm => ({
    filesExtensions: [
      {
        name: 'dxf',
        url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/dxf`,
        exts: ['dxf'],
        canExtDuplicate: false,
        minFiles: 1,
        maxFiles: 1,
      },
    ],
    layersListFetched: true,
    isImportDxfVisible: false,
    isImportDxfLoaded: false,
    dense: true,
    searchInput: {
      dataType: {
        name: 'text',
      },
      label: vm.$i18n.t('default.find'),
    },
    searchQuery: undefined,
    filteredLayers: [],
  }),
  computed: {
    dropzoneOptions() {
      const self = this;
      return {
        url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/dxf`,
        method: 'post',
        autoProcessQueue: false,
        acceptedFiles: '.dxf',
        uploadMultiple: true,
        maxFiles: 1,
        parallelUploads: 1,
        paramName(i) {
          const files = self.$refs.dxfFile?.$refs.dropzone?.getAcceptedFiles();
          const file = files[i];
          return file.name.split('.').pop();
        },
      };
    },
    importDxfInputs() {
      return [
        {
          name: 'dxfName',
          value: '',
          translationPath: 'dialog.name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: 50,
          },
        },
        {
          name: 'file',
          translationPath: 'dialog.chooseDxfFile',
          align: 'start',
          isCustom: true,
        },
      ];
    },
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    dxfLayers() {
      return this.layersArray.filter(layer => layer.layer_scope === 'dxf');
    },
  },
  methods: {
    getLayers: call('layers/getLayers'),
    handleImportDxf() {
      if (this.$refs.dxfFile.$refs.dropzone.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.fileRequired'),
        });
      } else {
        this.$refs.dxfFile.$refs.dropzone.processQueue();
      }
    },
    async onUploadSuccess() {
      this.layersListFetched = false;
      this.$refs.dxfAdding?.$refs.form?.reset();
      this.isImportDxfVisible = false;
      this.$refs.dxfFile?.$refs.dropzone?.removeAllFiles();
      await this.getLayers({ scope: 'dxf' });
      this.search(this.searchQuery);
      this.layersListFetched = true;
    },
    search(val) {
      if (val) {
        this.filteredLayers = this.dxfLayers.filter(layer => {
          return layer.name.toLowerCase().includes(val.toLowerCase());
        });
      } else {
        this.filteredLayers = [...this.dxfLayers];
      }
    },
    sendingEvent(files, xhr, formData) {
      formData.append('datasource_name', this.importDxfInputs.find(aDSI => aDSI.name === 'dxfName').value);
      formData.append('srid', 2178);
    },
    showImportDxf() {
      this.isImportDxfLoaded = true;
      this.isImportDxfVisible = true;
    },
  },
  watch: {
    isImportDxfVisible(nV) {
      if (!nV) {
        this.$refs.dxfFile?.$refs.dropzone?.removeAllFiles();
      }
    },
    dxfLayers(nV) {
      this.filteredLayers = [...nV];
    },
  },
  mounted() {
    this.$watch('searchQuery', debounce(this.search, 200));
    this.filteredLayers = [...this.dxfLayers];
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 32px !important;
      padding: 0 12px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field--outlined.v-input--dense .v-label {
      top: 8px;
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
